import React from 'react';
import { useTranslation } from 'react-i18next';
import coinIcon from '../icons/coin.png';
import heartFilledIcon from '../icons/heart-filled.png';

interface ActivityStatsProps {
  buyerCount: number;
  likeCount: number;
  followerCount: number;
  /** Host BEM block (e.g. "building-preview-card") so each surface keeps its own
   *  __stat / __dot styling. */
  classPrefix: string;
}

/**
 * Building activity row (coin · buyers, heart · likes, followers), with dot
 * dividers only between the items that are present. Styling is owned by each
 * host via `classPrefix`; stats hide at zero.
 */
const ActivityStats: React.FC<ActivityStatsProps> = ({
  buyerCount,
  likeCount,
  followerCount,
  classPrefix,
}) => {
  const { t } = useTranslation();

  const items: React.ReactNode[] = [];
  if (buyerCount > 0) {
    items.push(
      <span className={`${classPrefix}__stat`} key="buyers">
        <img className={`${classPrefix}__stat-coin`} src={coinIcon} alt="" aria-hidden="true" />
        {buyerCount}{' '}
        {buyerCount === 1
          ? t('building.view.buyer', 'buyer')
          : t('building.view.buyers', 'buyers')}
      </span>,
    );
  }
  if (likeCount > 0) {
    items.push(
      <span className={`${classPrefix}__stat`} key="likes">
        <img className={`${classPrefix}__stat-heart`} src={heartFilledIcon} alt="" aria-hidden="true" />
        {likeCount}
      </span>,
    );
  }
  if (followerCount > 0) {
    items.push(
      <span className={`${classPrefix}__stat`} key="followers">
        {followerCount}{' '}
        {followerCount === 1
          ? t('building.view.follower', 'follower')
          : t('building.view.followers', 'followers')}
      </span>,
    );
  }

  return (
    <>
      {items.flatMap((node, i) =>
        i === 0 ? [node] : [<span className={`${classPrefix}__dot`} key={`dot-${i}`} />, node],
      )}
    </>
  );
};

export default ActivityStats;
